import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAnal,
  useHapticFeedback,
  useModal,
  useTransaction,
} from '../../../../hooks'
import type { GameDataType, PlayerType } from '../../../../types'
import { ECurrency, EModalName } from '../../../../types/enums'
import { trpc } from '../../../../utils'
import { Scrollable } from '../../../atoms'
import { ModalBuyIn } from '../../../molecules'
import type { MultiTable } from '../../../organisms'
import type { UnhandledMessageType } from '../../../organisms/MultiTable/types'
import { MainLayout } from '../../../templates'
import { modalNewTableTab } from '../index.css'
import { getPlayerSeatReserveTime } from '../utils'

export const useMultiTable = (props: {
  userId: string
  currentTableId: string
  tables: {
    id: string
    password: string | null
  }[]
  onJoinNewTable: (table: { id: string; password: string | null }) => void
  onTableChange: (tableId: string) => void
  onUsersTurn: () => Promise<void>
}) => {
  const { hapticFeedback } = useHapticFeedback()
  const { t } = useTranslation()
  const { openModal, openInfoModal, openConfirmationModal } = useModal()
  const { logEvent } = useAnal()
  const { sendTransaction } = useTransaction()
  const [tonConnectUi] = useTonConnectUI()
  const userTonAddress = useTonAddress()

  const sendReserve = trpc.player.sendReserve.useMutation()
  const paymentInfo = trpc.table.getPaymentInfo.useMutation()
  const sendJoin = trpc.player.sendJoin.useMutation()
  const sendPayment = trpc.player.sendPayment.useMutation()
  const sendLeave = trpc.player.sendLeave.useMutation()

  const gameStateByTable = useRef<Record<string, GameDataType>>({})
  const userPlayerDataByTable = useRef<Record<string, PlayerType | null>>({})
  const messageQueueByTable = useRef<Record<string, UnhandledMessageType[]>>({})
  const userPlayerStateByTable = useRef<
    Record<
      string,
      | 'RESERVED'
      | 'BUYIN'
      | 'BUYIN_SHOWED'
      | 'PLAY'
      | 'REBUY'
      | 'REBUY_SHOWED'
      | 'LEFT'
      | null
    >
  >({})

  const [gameData, setGameData] = useState(
    gameStateByTable.current[props.currentTableId],
  )
  const [userPlayer, setUserPlayer] = useState(
    userPlayerDataByTable.current[props.currentTableId],
  )
  const [lastMessage, setLastMessage] = useState(
    messageQueueByTable.current[props.currentTableId]?.length
      ? messageQueueByTable.current[props.currentTableId][0]
      : null,
  )

  const standUpFromTable = () => {
    userPlayerStateByTable.current[props.currentTableId] = 'LEFT'
    const buyIn = userPlayer?.buyIn || 0
    sendLeave.mutate({
      tableId: props.currentTableId,
    })
    if (
      buyIn > 0 &&
      (gameData.info.currency === ECurrency.TON ||
        gameData.info.currency === ECurrency.USDT)
    ) {
      openInfoModal(
        {
          text: t('modal.info.withdrawal.text'),
          buttonText: t('modal.info.withdrawal.confirm'),
        },
        {
          title: t('modal.info.withdrawal.title'),
          name: EModalName.TABLE_WITHDRAWAL,
        },
      )
    }
    return true
  }

  const handleTransaction = async (
    tableId: string,
    data: {
      id: string
      currency: ECurrency
      address: string
      amount: string
      smartContractAddress?: string | undefined
    },
  ) => {
    try {
      const response = await sendTransaction(data)
      logEvent('transaction_done', {
        amount: data.amount,
        currency: data.currency,
      })
      sendPayment.mutate({
        tableId,
        data: {
          id: data.id,
          boc: response.boc,
          address: userTonAddress,
        },
      })
      openInfoModal(
        {
          text: t('modal.info.paymentProcessed.text'),
          buttonText: t('modal.info.paymentProcessed.confirm'),
        },
        {
          title: t('modal.info.paymentProcessed.title'),
          name: EModalName.TABLE_INFO_PAYMENT_PROCESSING,
        },
      )
    } catch (error) {
      if (error instanceof Error) {
        const { closeModal } = openConfirmationModal(
          {
            text: t('modal.confirmation.transactionError.text', {
              error: error.message,
            }),
            mainButtonText: t('modal.confirmation.transactionError.confirm'),
            secondaryButtonText: t(
              'modal.confirmation.transactionError.cancel',
            ),
          },
          {
            title: t('modal.confirmation.transactionError.title'),
            name: EModalName.CONFIRM_TRANSACTION_ERROR,
          },
        )
        const result = await closeModal
        if (result === 'main') {
          await tonConnectUi.disconnect()
          await tonConnectUi.openModal()
          if (userTonAddress) {
            await handleTransaction(tableId, data)
          }
        }
      } else {
        console.error(error)
      }
    }
  }

  const handlePayment = async (tableId: string, buyIn: number) => {
    const data = await paymentInfo.mutateAsync({
      tableId,
      buyIn,
    })
    if (data) {
      await handleTransaction(tableId, data)
    }
  }

  const handleShowBuyInModal = async (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => {
    if (!userPlayer) {
      return
    }

    const isSystemCryptoCurrency =
      data.info.currency === ECurrency.TON ||
      data.info.currency === ECurrency.USDT
    if (isSystemCryptoCurrency && !userTonAddress) {
      await tonConnectUi.openModal()
      if (!userTonAddress) {
        return
      }
    }

    const { closeModal } = openModal<number | undefined>(
      ({ close }) => (
        <ModalBuyIn
          currency={data.info.currency}
          reserveMs={getPlayerSeatReserveTime(userPlayer, data.info.type)}
          minBuyIn={data.info.minBuyIn}
          maxBuyIn={data.info.maxBuyIn}
          close={close}
        />
      ),
      {
        initialValue: undefined,
        title: t('modal.buyIn.header'),
        name: EModalName.TABLE_BUY_IN,
      },
    )
    const buyIn = await closeModal
    if (buyIn === undefined) {
      if (
        !isSystemCryptoCurrency &&
        (userPlayer.status === 'IDLE' || userPlayer.status === 'RESERVE')
      ) {
        standUpFromTable()
      }
      return
    }
    if (isSystemCryptoCurrency) {
      await handlePayment(tableId, buyIn)
    } else {
      await sendJoin.mutateAsync({
        tableId,
        buyIn,
      })
    }
  }

  const handleShowReBuyInModal = async (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => {
    if (!data) {
      console.warn(
        'Attempted to show re-buy-in modal before data was available.',
      )
      return
    }

    if (!userPlayer) {
      console.warn('Attempted to show re-buy-in modal without userPlayer.')
      return
    }

    const { closeModal } = openConfirmationModal(
      {
        text: t('modal.confirmation.zeroBalance.text'),
        mainButtonText: t('modal.confirmation.zeroBalance.confirm'),
        secondaryButtonText: t('modal.confirmation.zeroBalance.cancel'),
      },
      {
        title: t('modal.confirmation.zeroBalance.title'),
        name: EModalName.TABLE_INSUFFICIENT_BALANCE,
      },
    )
    const result = await closeModal
    if (result === 'main') {
      await handleShowBuyInModal(tableId, data, userPlayer)
      return
    }
    standUpFromTable()
  }

  const handleUserPlayerTurnNotification = async (
    tableId: string,
    playerTurnId: string,
  ) => {
    const lastPlayerTurnId =
      gameStateByTable.current[tableId]?.table?.playerTurnId
    if (
      (!lastPlayerTurnId || lastPlayerTurnId !== playerTurnId) &&
      playerTurnId === props.userId
    ) {
      hapticFeedback({ impact: 'soft' })
      await props.onUsersTurn()
    }
  }

  const handleUpdateUserStatus = (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => {
    const userPlayerState = userPlayerStateByTable.current[tableId]
    if (!data || userPlayer === undefined) {
      return null
    }

    if (userPlayer === null) {
      const isSystemCryptoCurrency =
        data.info.currency === ECurrency.TON ||
        data.info.currency === ECurrency.USDT
      if (
        !userPlayerStateByTable.current[tableId] &&
        userPlayerState !== 'LEFT' &&
        (!isSystemCryptoCurrency || userTonAddress)
      ) {
        return 'RESERVED'
      }
      return null
    }

    if (userPlayer.buyIn > 0 || userPlayer.status === 'PLAY') {
      return 'PLAY'
    }
    if (userPlayer.status === 'PAYMENT') {
      return 'PLAY'
    }
    if (userPlayer.status === 'IDLE') {
      if (userPlayer.buyIn === 0) {
        return 'REBUY'
      }
      return null
    }
    if (userPlayer.status === 'RESERVE') {
      return 'BUYIN'
    }
    return null
  }

  const handleUserStatus = async (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => {
    const userPlayerState = userPlayerStateByTable.current[tableId]
    const newUserState = handleUpdateUserStatus(tableId, data, userPlayer)
    if (newUserState && userPlayerState !== newUserState) {
      userPlayerStateByTable.current[tableId] = newUserState

      if (newUserState === 'RESERVED') {
        sendReserve.mutate({
          tableId,
        })
        return
      }

      if (newUserState === 'PLAY') {
        if (data.info.type === 'PUBLIC') {
          const isFree = data.info.currency === 'COIN'
          logEvent(
            `join_public_table_${isFree ? 'free' : data.info.currency.toLowerCase()}`,
          )
        }
        return
      }
      if (tableId !== props.currentTableId) {
        return
      }
      if (newUserState === 'BUYIN') {
        if (userPlayerState === 'BUYIN_SHOWED') {
          return
        }
        await handleShowBuyInModal(tableId, data, userPlayer)
        return
      }
      if (newUserState === 'REBUY') {
        if (userPlayerState === 'REBUY_SHOWED') {
          return
        }
        await handleShowReBuyInModal(tableId, data, userPlayer)
        return
      }
    }
  }

  const handleRefsUpdate = () => {
    setGameData(gameStateByTable.current[props.currentTableId])
    setUserPlayer(userPlayerDataByTable.current[props.currentTableId])
    setLastMessage(
      messageQueueByTable.current[props.currentTableId]?.length
        ? messageQueueByTable.current[props.currentTableId][0]
        : null,
    )
  }

  const handleTableDeletion = (tableId: string) => {
    delete gameStateByTable.current[tableId]
    delete userPlayerDataByTable.current[tableId]
    delete messageQueueByTable.current[tableId]
    delete userPlayerStateByTable.current[tableId]
  }

  const onClickNewTab = () => {
    openModal(
      ({ close }) => (
        <Scrollable className={modalNewTableTab}>
          <MainLayout
            type='newTableTab'
            onJoinNewTable={(table) => {
              props.onJoinNewTable(table)
              close()
            }}
          />
        </Scrollable>
      ),
      {
        title: t('modal.newTableTab.header'),
        position: 'bottom',
        closeOnClickOutside: true,
        name: EModalName.NEW_TABLE_TAB,
      },
    )
  }

  const onUpdate = async (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => {
    await handleUserStatus(tableId, data, userPlayer)

    if (
      userPlayer?.status === 'PLAY' &&
      gameStateByTable.current[tableId]?.table?.handNumber !==
        data.table.handNumber
    ) {
      logEvent('play_hand')
    }
    if (data.table.playerTurnId !== null) {
      await handleUserPlayerTurnNotification(tableId, data.table.playerTurnId)
    }
    gameStateByTable.current[tableId] = data
    userPlayerDataByTable.current[tableId] = userPlayer
    if (tableId === props.currentTableId) {
      handleRefsUpdate()
    }
  }

  const onHandleQueueMessage = () => {
    if (messageQueueByTable.current[props.currentTableId]?.length) {
      messageQueueByTable.current[props.currentTableId].shift()
      setLastMessage(
        messageQueueByTable.current[props.currentTableId]?.length
          ? messageQueueByTable.current[props.currentTableId][0]
          : null,
      )
    }
  }

  const onUnhandledUpdate = (tableId: string, data: UnhandledMessageType) => {
    if (!(tableId in messageQueueByTable.current)) {
      messageQueueByTable.current[tableId] = []
    }
    messageQueueByTable.current[tableId].push(data)
    if (props.currentTableId === tableId) {
      setLastMessage(messageQueueByTable.current[tableId][0])
    }
  }

  const onTableChange = async (tableId: string) => {
    props.onTableChange(tableId)
    const userPlayerState = userPlayerStateByTable.current[tableId]
    const userPlayer = userPlayerDataByTable.current[tableId]
    const data = gameStateByTable.current[tableId]
    if (!data || !userPlayer) {
      return
    }
    if (userPlayerState === 'BUYIN') {
      userPlayerStateByTable.current[tableId] = 'BUYIN_SHOWED'
      await handleShowBuyInModal(tableId, data, userPlayer)
      return
    }
    if (userPlayerState === 'REBUY') {
      userPlayerStateByTable.current[tableId] = 'REBUY_SHOWED'
      await handleShowReBuyInModal(tableId, data, userPlayer)
      return
    }
  }

  useEffect(() => {
    handleRefsUpdate()
  }, [props.currentTableId])

  useEffect(() => {
    const newTableIds = new Set(props.tables.map((table) => table.id))
    for (const openedTableId of Object.keys(gameStateByTable.current)) {
      if (!newTableIds.has(openedTableId)) {
        handleTableDeletion(openedTableId)
      }
    }
  }, [props.tables])

  return {
    gameData,
    userPlayer,
    lastMessage,
    multiTableProps: {
      userId: props.userId,
      currentTableId: props.currentTableId,
      tables: props.tables,
      onUpdate: onUpdate,
      onUnhandledUpdate: onUnhandledUpdate,
      onSelect: onTableChange,
      onClickNewTab: onClickNewTab,
    } satisfies React.ComponentProps<typeof MultiTable>,
    standUpFromTable,
    onHandleQueueMessage,
  }
}
